import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Info, Heart, Database, Zap, Shield, Mail, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { trackButtonClick } from "@/services/analytics";

const About = () => {
  const navigate = useNavigate();

  // Add page-specific meta tags and schema markup for SEO
  usePageMeta({
    title: "About Let's Be Real | Property Insights Platform",
    description: "Learn how Let's Be Real provides brutally honest property address analysis using Census 2021, real-time HERE Transit API, places data, routing, crime statistics, and AI. Your property research tool for neighborhood insights.",
    canonical: "https://letsbereal.com.au/about",
    ogImage: "https://letsbereal.com.au/lbr.png",
    ogType: "website",
    twitterCard: "summary",
    keywords: "about us, property analysis methodology, census data, HERE API, real estate insights, AI property reports, australian property data",
    schema: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Let's Be Real",
      "description": "Information about Let's Be Real property insights platform, our methodology, data sources, and contact information",
      "url": "https://letsbereal.com.au/about",
      "mainEntity": {
        "@type": "Organization",
        "name": "Let's Be Real",
        "url": "https://letsbereal.com.au",
        "logo": "https://letsbereal.com.au/lbr.png",
        "email": "help.letsbereal@gmail.com",
        "description": "Property insights platform providing honest, data-driven analysis using Census 2021, SEIFA indexes, property sales data, crime statistics, building approvals, HERE real-time transit/places/routing APIs, and ChatGPT AI",
        "areaServed": {
          "@type": "Country",
          "name": "Australia"
        }
      }
    }
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4 mb-4">
            <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-primary/10 flex-shrink-0">
              <Info className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">About Let's Be Real</h1>
              <p className="text-xs sm:text-sm md:text-base text-muted-foreground mt-0.5 sm:mt-1">
                Property address analysis with zero real estate BS
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Mission */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4 mb-4">
                <Heart className="h-6 w-6 text-red-500 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold mb-3">Our Mission</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Let's Be Real was created to cut through the real estate marketing garbage and give you honest property insights about Australian addresses. We think property buyers and renters deserve the truth, not just what sounds pretty in a listing.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    Using real Census 2021 data, SEIFA socioeconomic indexes, actual property sales, crime statistics, building approvals, HERE real-time transit data (all Australian cities), places/amenities, routing/commute times, OpenStreetMap, and AI analysis, we generate property location reports that tell you what the neighborhood around your address is actually like, not what the agent wants you to hear.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    Enter an address, get the brutal truth. Simple as that.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What We Do */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold mb-4">What We Analyze</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🚆</span>
                    <div>
                      <h3 className="font-semibold">Accessibility</h3>
                      <p className="text-sm text-muted-foreground">
                        Real-time transit data for all Australian cities (trains, metros, trams, ferries, buses). Actual service frequencies, walking times, and CBD commutes.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🔊</span>
                    <div>
                      <h3 className="font-semibold">Noise & Traffic</h3>
                      <p className="text-sm text-muted-foreground">
                        Honest assessment of road noise and congestion
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🏗️</span>
                    <div>
                      <h3 className="font-semibold">Local Development</h3>
                      <p className="text-sm text-muted-foreground">
                        Real building approvals, construction trends, and growth activity
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🌳</span>
                    <div>
                      <h3 className="font-semibold">Environment</h3>
                      <p className="text-sm text-muted-foreground">
                        Green space and local crime statistics. Is it safe? Is it pretty?
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">👥</span>
                    <div>
                      <h3 className="font-semibold">Demographics</h3>
                      <p className="text-sm text-muted-foreground">
                        Neighborhood demographics using Census 2021 + SEIFA indexes. Who are your future neighbors?
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">📈</span>
                    <div>
                      <h3 className="font-semibold">Market Dynamics</h3>
                      <p className="text-sm text-muted-foreground">
                        Property market insights and investment property analysis. What's the money doing?
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">☕</span>
                    <div>
                      <h3 className="font-semibold">Gentrification Index</h3>
                      <p className="text-sm text-muted-foreground">
                        How the area is changing (hipster factor)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* How It Works */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4 mb-4">
                <Zap className="h-6 w-6 text-yellow-500 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold mb-3">How It Works</h2>
                  <ol className="space-y-3 text-muted-foreground">
                    <li className="flex gap-3">
                      <span className="font-bold text-foreground">1.</span>
                      <span>You enter an Australian address</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-foreground">2.</span>
                      <span>We geocode the location using HERE Geocoding API</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-foreground">3.</span>
                      <span>We fetch real-time data: transit schedules (HERE), nearby places/amenities (HERE), walking times, CBD commutes (HERE Routing), and road infrastructure (OpenStreetMap)</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-foreground">4.</span>
                      <span>We enrich with ABS Census 2021 data, SEIFA socioeconomic indexes, property sales trends, crime statistics, and building approvals</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-foreground">5.</span>
                      <span>Our AI (ChatGPT) synthesizes all data sources into honest, comprehensive insights</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold text-foreground">6.</span>
                      <span>You get a detailed report with 5-6 data-rich insights per category in under a minute</span>
                    </li>
                  </ol>
                  <p className="text-muted-foreground leading-relaxed mt-4 pt-4 border-t border-border">
                    <strong className="text-foreground">Important distinction:</strong> Drop in an address. We grab data for the surrounding area (think ~1km radius, not just the suburb). You get neighborhood insights for that specific location, not a generic suburb profile. Big difference.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Sources */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4 mb-4">
                <Database className="h-6 w-6 text-blue-500 mt-1" />
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-3">Our Data Sources</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We combine real-time HERE APIs, ABS Census 2021, OpenStreetMap, and ChatGPT AI analysis for comprehensive property insights.
                  </p>
                  
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="data-sources" className="border rounded-lg">
                      <AccordionTrigger className="px-4 hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold">View Detailed Data Sources</span>
                          <Badge variant="secondary">7 sources</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-4 pt-2">
                          {/* HERE APIs - Real-time Data */}
                          <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-primary">
                            <h3 className="font-semibold text-foreground mb-2">🌐 HERE APIs - Enterprise Real-Time Data</h3>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">HERE Transit API:</strong> Real-time transit schedules for all Australian cities (Sydney, Melbourne, Brisbane, Perth, Adelaide, Canberra, Hobart, Darwin). Includes trains, metros, trams, ferries, and buses with live frequencies.</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">HERE Places API:</strong> Current POI data - cafes, restaurants, supermarkets, schools, hospitals, parks. Updated frequently.</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">HERE Routing API:</strong> Walking times to nearest station, multi-modal commute times to CBD, accurate journey planning.</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">HERE Geocoding API:</strong> Precise address geocoding and autocomplete for Australian addresses.</span>
                              </li>
                            </ul>
                          </div>

                          {/* OpenStreetMap Data */}
                          <div className="p-4 bg-muted/30 rounded-lg">
                            <h3 className="font-semibold text-foreground mb-2">🗺️ OpenStreetMap</h3>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">Overpass API:</strong> Road infrastructure data (major roads, motorways) for noise assessment. Community-maintained, frequently updated.</span>
                              </li>
                            </ul>
                          </div>

                          {/* Government Data */}
                          <div className="p-4 bg-muted/30 rounded-lg">
                            <h3 className="font-semibold text-foreground mb-2">🏛️ Official Government Data</h3>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">ABS Census 2021:</strong> Official demographic statistics (income, age, education, employment) for 330 SA2 areas</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">ABS SEIFA 2021:</strong> Socioeconomic Indexes for Areas (advantage/disadvantage, education, economic resources)</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">Property Sales Data:</strong> Real median prices, YoY growth, sales volume, and days on market for 309 suburbs (2024 Q3)</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">Crime Statistics:</strong> Official crime rates, trends, and state comparisons for 309 suburbs (2024 Q3)</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">Building Approvals:</strong> Quarterly development data, dwelling units, construction value for 322 SA2 areas (2024 Q3)</span>
                              </li>
                            </ul>
                          </div>

                          {/* AI Analysis */}
                          <div className="p-4 bg-muted/30 rounded-lg">
                            <h3 className="font-semibold text-foreground mb-2">🤖 AI-Powered Analysis</h3>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                              <li className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span><strong className="text-foreground">ChatGPT:</strong> Advanced AI with city-specific prompting (Sydney, Melbourne, Brisbane, Perth, Adelaide, Canberra). Synthesizes all data into honest, comprehensive insights with Australian personality.</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                  
                  <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <p className="text-sm text-foreground leading-relaxed">
                      <strong>🎯 Our Commitment:</strong> We use enterprise-grade HERE APIs for real-time data accuracy, combined with free government datasets and open platforms. Government data is curated and stored locally for fast access. Live data is fetched per request for maximum freshness.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Important Disclaimer */}
          <Card className="border-yellow-500/30 bg-yellow-500/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <Shield className="h-6 w-6 text-yellow-600 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold mb-3 text-yellow-700">Important Disclaimer</h2>
                  <div className="space-y-2 text-muted-foreground">
                    <p className="leading-relaxed">
                      <strong className="text-foreground">This is a satirical tool for entertainment purposes.</strong> Our 
                      reports use real data but are presented with humor and should not be your only 
                      source of property information.
                    </p>
                    <p className="leading-relaxed">
                      <strong className="text-foreground">Do NOT make life-changing property decisions based solely on our reports.</strong> Always:
                    </p>
                    <ul className="space-y-1 ml-6 list-disc">
                      <li>Visit properties in person</li>
                      <li>Conduct thorough inspections</li>
                      <li>Consult qualified professionals (real estate agents, lawyers, financial advisors)</li>
                      <li>Research market trends and pricing from reputable sources</li>
                      <li>Consider your personal needs and circumstances</li>
                    </ul>
                  <p className="leading-relaxed mt-3">
                    We're here to provide an honest, data-driven perspective, but we're not a 
                    replacement for professional advice.
                  </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-2xl">
                <Mail className="h-6 w-6 text-primary" />
                Get In Touch
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed mb-6">
                We'd love to hear from you! Whether you found a bug, have a suggestion, or just 
                want to say hi, reach out via email.
              </p>

              {/* Email Contact */}
              <div className="p-6 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20 mb-6">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong className="text-foreground text-base">Email us directly at:</strong>
                </p>
                <a 
                  href="mailto:help.letsbereal@gmail.com"
                  onClick={() => trackButtonClick('contact_email', 'about_page')}
                  className="text-2xl sm:text-3xl font-semibold text-primary hover:underline break-all"
                >
                  help.letsbereal@gmail.com
                </a>
              </div>

              {/* Response Time */}
              <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <h3 className="font-semibold mb-2 text-sm">⏰ Response Times</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Let's Be Real is a side project. We aim to respond to all inquiries within:
                </p>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0"></span>
                    <span><strong className="text-foreground">Bug reports:</strong> 24-48 hours</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0"></span>
                    <span><strong className="text-foreground">General inquiries:</strong> 2-5 business days</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-purple-500 flex-shrink-0"></span>
                    <span><strong className="text-foreground">Feature requests:</strong> We'll read them all!</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* FAQ Link */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <HelpCircle className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-semibold">Have more questions?</p>
                    <p className="text-sm text-muted-foreground">Common questions answered in our FAQ</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/faq")}
                  className="gap-2"
                >
                  View FAQ
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default About;

